import { Selectable } from "common/selection/state/selectable.model";
import { SelectionStoreService } from "common/selection/state/selection-store.service";
import TypedId = stages.core.TypedId;

export function getUpdatedSelectables(
	selectablesToAdd: Selectable[],
	selectablesToRemove: Selectable[],
	updatedSelectables: Selectable[],
): stages.process.AssociationsContainer {
	const addableTypedIds: TypedId[] = [];
	const removableTypedIds: TypedId[] = [];

	selectablesToAdd.forEach((selectable) => {
		addableTypedIds.push({ id: selectable.id, typeIdent: selectable.type });
	});

	selectablesToRemove.forEach((selectable) => {
		removableTypedIds.push({ id: selectable.id, typeIdent: selectable.type });
	});

	return {
		added: addableTypedIds,
		removed: removableTypedIds,
		modified: getAssociationDetails(updatedSelectables),
	};
}

export function isInSelection(selections: Selectable[], selectable: Selectable): boolean {
	for (const selection of selections) {
		if (getIdKeyOfSelectable(selection) === selectable.typeId) {
			return true;
		}
	}
	return false;
}

export function createBasicSelectablesFromTreeElements(
	treeElements: stages.process.TreeElement[],
	storeIdentifier: string,
): Selectable[] {
	const selectables: Selectable[] = [];

	const storeIdentifierWithDefault = storeIdentifier ?? SelectionStoreService.defaultStoreIdentifier;

	treeElements.forEach((element) => {
		const selectable: Selectable = {
			id: element.elementId,
			type: element.elementType.toLocaleLowerCase(),
			typeId: getIdKeyOfTypeId({ id: element.elementId, typeIdent: element.elementType }),
			identity:
				storeIdentifierWithDefault + "_" + getIdKeyOfTypeId({ id: element.elementId, typeIdent: element.elementType }),
			parentId: element.parentId ? element.parentId : null,
			parentType: element.parentType?.toLocaleLowerCase() ?? null,
			storeIdentifier: storeIdentifierWithDefault,
			initiallySelected: false, // recalculated later in createSelectables for dynamic stores
			indeterminate: false,
			selectableChildrenCount: null,
			initiallySelectedCount: null,
			totalChildrenCount: null,
			isSelectable: element.selectable,
			isSelected: false, // recalculated later in createSelectables for dynamic stores
			comment: null, // until now no comments possible / needed in dynamic store usages
			isCommentEditable: false, // until now no comments possible / needed in dynamic store usages
			wasUpdated: false,
		};

		selectables.push(selectable);
	});
	return selectables;
}

// TODO introduce new BasicSelectalbe vs / ExtendedSelectable
export function createBasicSelectablesFromTypeIds(
	selectedTypeIDs: stages.core.TypedId[],
	storeIdentifier: string,
): Selectable[] {
	const selectables: Selectable[] = [];
	const storeIdentifierWithDefault = storeIdentifier ?? SelectionStoreService.defaultStoreIdentifier;

	selectedTypeIDs.forEach((typeId) => {
		const selectable: Selectable = {
			id: typeId.id,
			type: typeId.typeIdent,
			typeId: getIdKeyOfTypeId({ id: typeId.id, typeIdent: typeId.typeIdent }),
			identity: storeIdentifierWithDefault + "_" + getIdKeyOfTypeId({ id: typeId.id, typeIdent: typeId.typeIdent }),
			parentId: null,
			parentType: null,
			storeIdentifier: storeIdentifierWithDefault,
			initiallySelected: true, // recalculated later in createSelectables for dynamic stores
			indeterminate: false,
			selectableChildrenCount: null,
			initiallySelectedCount: null,
			totalChildrenCount: null,
			isSelectable: true,
			isSelected: true, // recalculated later in createSelectables for dynamic stores
			comment: null, // until now no comments possible / needed in dynamic store usages
			isCommentEditable: false, // until now no comments possible / needed in dynamic store usages
			wasUpdated: false,
		};

		selectables.push(selectable);
	});
	return selectables;
}

export function getIdKeyOfSelectable(selection: Selectable): string {
	return selection.type.toLocaleLowerCase() + "_" + selection.id;
}

export function getIdKeyOfTypeId(typeId: TypedId): string {
	return typeId.typeIdent.toLocaleLowerCase() + "_" + typeId.id;
}

export function getTypeIdOfTypeIdString(key: string): TypedId | undefined {
	const strParts = key.split("_");
	if (strParts[0] && strParts[1]) {
		return { typeIdent: strParts[0], id: strParts[1] };
	}
	return undefined;
}

export function getIdIdentity(storeIdentifier: string, typeId: TypedId): string {
	return storeIdentifier + "_" + getIdKeyOfTypeId(typeId);
}

function getAssociationDetails(updatedSelectables: Selectable[]): stages.core.model.TypedIdAssociationDetails[] {
	const modifiedComments: stages.core.model.TypedIdAssociationDetails[] = [];
	updatedSelectables.forEach((updatedSelectable: Selectable) => {
		const modifiedComment = {
			typeIdent: updatedSelectable.type,
			id: updatedSelectable.id,
			comment: updatedSelectable.comment,
		};
		modifiedComments.push(modifiedComment);
	});
	return modifiedComments;
}
